import React, { useContext, useState } from "react";
import { Table, Space, message, Modal, Button, Tag } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { api, API_BASE_URL } from "../../config/api";
import { useMyContext } from "../../context/MyContext";
import CurrencyEditModal from "./currencyEditModal";
import { CurrencyContext } from "../../context/CurrencyContext";

const CurrencyTable = () => {
  const { token } = useMyContext();
  const { currencies, fetchCurrencies } = useContext(CurrencyContext);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const showDeleteModal = (record) => {
    setSelectedCurrency(record);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    if (!selectedCurrency) return;

    try {
      const response = await api.delete(
        `${API_BASE_URL}/currency/${selectedCurrency._id}`
      );

      if (response.status === 200) {
        message.success("Currency deleted successfully!");
        fetchCurrencies(); // Refresh the list
      } else {
        message.error("Failed to delete currency.");
      }
    } catch (error) {
      message.error("Server error: " + error.message);
    } finally {
      setIsDeleteModalVisible(false);
      setSelectedCurrency(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setSelectedCurrency(null);
  };

  const handleEdit = (record) => {
    setSelectedCurrency(record);
    setIsEditModalVisible(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalVisible(false);
    setSelectedCurrency(null);
  };

  // Helper function to render exchange rates
  const renderExchangeRates = (exchangeRates, baseCurrency) => {
    if (!exchangeRates) return "No rates available";

    const rates = [];
    Object.entries(exchangeRates).forEach(([currency, rate]) => {
      if (currency !== baseCurrency && rate !== null && rate !== undefined) {
        rates.push(
          <Tag key={currency} color="blue" style={{ margin: "2px" }}>
            {currency}: {typeof rate === 'number' ? rate.toFixed(4) : rate}
          </Tag>
        );
      }
    });

    return rates.length > 0 ? <div>{rates}</div> : "No exchange rates set";
  };

  const columns = [
    {
      title: "Currency Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <span>
          <strong>{text}</strong> ({record.symbol})
        </span>
      ),
    },
    {
      title: "Exchange Rates",
      key: "exchangeRates",
      render: (_, record) => renderExchangeRates(record.exchangeRates, record.name),
      width: "40%",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => {
        if (!text) return "N/A";
        const date = new Date(text);
        return isNaN(date.getTime())
          ? "Invalid Date"
          : date.toISOString().split("T")[0];
      },
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text) => {
        if (!text) return "Never updated";
        const date = new Date(text);
        return isNaN(date.getTime())
          ? "Invalid Date"
          : date.toISOString().split("T")[0];
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <EditOutlined
            style={{ color: "blue", cursor: "pointer" }}
            onClick={() => handleEdit(record)}
            title="Edit exchange rates"
          />
          <DeleteOutlined
            style={{ color: "red", cursor: "pointer" }}
            onClick={() => showDeleteModal(record)}
            title="Delete currency"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={currencies.map((currency, index) => ({
          ...currency,
          key: currency._id || index,
        }))}
        loading={loading}
        pagination={{ pageSize: 5 }}
        scroll={{ x: 800 }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onCancel={handleCancelDelete}
        footer={[
          <Button key="cancel" onClick={handleCancelDelete}>
            No
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleDelete}>
            Yes
          </Button>,
        ]}
        maskClosable={true}
        closable={true}
      >
        <p>
          Are you sure you want to delete "{selectedCurrency?.name}" and all its exchange rates?
        </p>
      </Modal>

      {/* Edit Currency Modal */}
      {isEditModalVisible && selectedCurrency && (
        <CurrencyEditModal
          showModal={isEditModalVisible}
          setShowModal={setIsEditModalVisible}
          currency={selectedCurrency}
          onUpdate={fetchCurrencies}
        />
      )}
    </>
  );
};

export default CurrencyTable;